import Image, {ImageLoaderProps, ImageProps as NextImageProps, StaticImageData} from 'next/image';
import React, {CSSProperties, useState} from 'react';

// Extend Next.js 13 Image props
type Props = Omit<NextImageProps, 'src' | 'alt' | 'loader'> & {
  src: string | StaticImageData;
  alt?: string;
  fallbacksrc?: string;
  loader?: (props: ImageLoaderProps) => string;
  objectFit?: CSSProperties['objectFit'];
};

type SrcImageType = string | StaticImageData;

const formatAltFrom = (src: SrcImageType): string => {
  const finalSrc = typeof src === 'string' ? src : src.src;
  const result = finalSrc.match(/\/([^/.]+)\./);
  return result?.[1] || 'image';
};

/**
 * Optimized Next.js 13 Image component with fallback support
 * Features:
 * - Native lazy loading for below-the-fold images
 * - Automatic format optimization (WebP/AVIF)
 * - Proper error handling with fallback
 * - Optimized for LCP performance
 * - No unnecessary re-renders
 *
 * @see https://nextjs.org/docs/app/api-reference/components/image
 */
const NextImageUI = ({fallbacksrc, alt, src, style, ...props}: Props) => {
  const [imgSrc, setImgSrc] = useState(src);
  const [isError, setIsError] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  const altText = alt || formatAltFrom(src);

  const handleError = () => {
    if (!isError && fallbacksrc) {
      setIsError(true);
      setImgSrc(fallbacksrc);
      setIsLoading(false);
    }
  };

  // Merge styles for better performance
  const imageStyle: CSSProperties = {
    ...style,
    objectFit: props.objectFit || undefined,
  };

  if (!src) return null;

  // Auto-detect optimal quality based on image context
  const getAutoQuality = (): number => {
    if (props.quality !== undefined) {
      return typeof props.quality === 'number' ? props.quality : parseInt(String(props.quality), 10);
    }

    const srcStr = typeof src === 'string' ? src : src.src;

    // Aggressive compression for product thumbnails and list views
    if (srcStr.includes('thumbnail') || srcStr.includes('list')) return 50;
    if (srcStr.includes('showcase') || srcStr.includes('banner')) return 60;
    if (srcStr.includes('logo') || srcStr.includes('profile')) return 70;
    if (srcStr.includes('icon') || srcStr.includes('svg')) return 80;

    return 65; // Default: balanced compression
  };

  const autoQuality = getAutoQuality();

  // Lazy load by default unless priority is set
  const loadingStrategy = props.loading || (props.priority ? 'eager' : 'lazy');

  return (
    <div style={props.fill ? {position: 'relative', width: '100%', height: '100%'} : {}}>
      {isLoading && fallbacksrc ? (
        <Image {...props} src={fallbacksrc} alt="Placeholder" quality={10} sizes="(max-width: 320px) 100vw, 320px" />
      ) : null}

      <Image
        {...props}
        src={imgSrc}
        alt={altText}
        loading={loadingStrategy}
        style={imageStyle}
        quality={autoQuality}
        onError={handleError}
        onLoad={() => setIsLoading(false)}
      />
    </div>
  );
};

export default NextImageUI;
